import { useEffect, useState } from 'react';

/**
 * A floating "back to top" button for the long dashboard lists.
 *
 * Shown as soon as the page is scrolled at all, and gone at the top, where it
 * would do nothing. 48px, past the 44px floor.
 *
 * **It never sits over content, at any width.** A fixed button covers whatever
 * scrolls beneath it unless there is somewhere empty for it to be, so:
 *
 * - **From 1660px wide** the gutter beside the 96rem page is wide enough, and
 *   the button sits there, outside the page, by at least 15px.
 * - **Narrower** it sits 12px in from the right edge, and every row, header and
 *   card that can scroll beneath it keeps a lane clear on its right (`pr-lane`
 *   in index.css). The lane is sized to the button: 3rem inside the page's own
 *   1.5rem margin from `lg`, 4.5rem below `lg`, where the page runs to the edge.
 *
 * A page that uses this must give its scrolling content `pr-lane` in place of
 * its right padding.
 *
 * Returning to the top also returns focus there: the button disappears when it
 * has done its job, and focus left on a removed element falls to the document.
 */
export function ScrollToTop() {
  const [away, setAway] = useState(false);

  useEffect(() => {
    const onScroll = () => setAway(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!away) return null;

  const toTop = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    const heading = document.querySelector<HTMLElement>('h1');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  };

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label="Back to top"
      title="Back to top"
      className="fixed right-3 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-20 flex size-12
                 items-center justify-center rounded-full bg-neutral-900 text-xl text-white shadow-lg
                 ring-2 ring-white hover:bg-neutral-700 active:bg-neutral-700
                 min-[1660px]:right-[calc((100vw-96rem)/2-3rem)]"
    >
      <span aria-hidden="true">↑</span>
    </button>
  );
}
